import type {
  QueryFunctionContext,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import groq from 'groq';
import {
  ASSET,
  BANNER_BLOCK,
  BEFORE_AFTER_BLOCK,
  BENEFITS_V2_BLOCK,
  CATEGORIES_INTRO_BLOCK,
  CERTIFICATION_BLOCK,
  CHART_BLOCK,
  COALESCING_PRODUCT_VARIANT,
  COMPARISON_TABLE_BLOCK,
  FAQ,
  FEATURE_LIST_BLOCK,
  HALO_SHOWCASE_BLOCK,
  ICONS_LIST_BLOCK,
  IMAGE_WITH_CALLOUTS_BLOCK,
  KEY_INGREDIENTS_BLOCK,
  MEDIA_BLOCK,
  MITOCHONDRIAL_DECLINE_BLOCK,
  MITOPURE_SOLUTION_BLOCK,
  MITOPURE_TRANSFORMATION_BLOCK,
  PRESS_QUOTES_BLOCK,
  PRODUCT_ACCORDION_FIELDS,
  PRODUCT_COMPARISON_BLOCK,
  PRODUCT_SLUG,
  REVIEWS_CAROUSEL_BLOCK,
  RICH_TEXT,
  SEE_IT_IN_ACTION,
  SLUG,
  SOCIAL_MEDIA_CAROUSEL_BLOCK,
  TESTIMONIAL_GROUP_BLOCK,
  TEXT_BLOCK,
  TEXT_MEDIA_SPLIT_BLOCK,
  VIBE_GALLERY_BLOCK,
} from 'lib/queries';
import { PLP_HREF } from 'lib/queries/common/plpHref';
import { PRODUCT_FORMAT_FIELDS } from 'lib/queries/product/productFormat';
import { productHref } from 'lib/queries/product/productHref';
import { PRODUCT_MEDIA_FULL_LIST } from 'lib/queries/product/productMedia';
import { client } from 'lib/sanity';
import type { ProductPage } from 'types/cms/pages/ProductPage';

type ProductPageQuery = UseQueryResult & {
  data: ProductPage;
};

export const PRODUCT_PAGE_KEY = 'productPage';

type ProductPageQueryKey = [string, string];

export const fetchProductPage = async ({
  queryKey,
}: QueryFunctionContext<ProductPageQueryKey>) => {
  const query = groq`
    *[_type in ["product"] && !(_id in path('drafts.**')) && store.slug.current == $slug] {
      category,
      "media": ${PRODUCT_MEDIA_FULL_LIST},
      "mostPopularVariantId": mostPopular->store.gid,
      "bestValueVariantId": bestValue->store.gid,
      longDescription[] { ${RICH_TEXT} },
      frontrowMD {
        "productId": coalesce(^.frontrowMDProductId, null),
        "isFrontrowBadgeEnabled": coalesce(isFrontrowBadgeEnabled, false),
        "isFrontrowTestimonialsEnabled": coalesce(isFrontrowTestimonialsEnabled, false),
        "isFrontrowClinicianAIEnabled": coalesce(isFrontrowClinicianAIEnabled, false)
      },
      "productType": store.productType,
      relatedProducts {
        title,
        "products": productCollection -> {...}.products[].product -> { ${PRODUCT_SLUG} },
        "plp": plpRef-> {
          label,
          ctaLabel,
          ${SLUG},
          ${PLP_HREF}
        }
      },
      "productFormats": productFormats->products[] {
        product-> { ${PRODUCT_FORMAT_FIELDS} },
        ${COALESCING_PRODUCT_VARIANT}
      },
      blocks[] {
        _key,
        _ref,
        _type,
        ${MITOPURE_SOLUTION_BLOCK},
        ${CERTIFICATION_BLOCK},
        ${ICONS_LIST_BLOCK},
        ${BENEFITS_V2_BLOCK},
        ${TEXT_MEDIA_SPLIT_BLOCK},
        ${BEFORE_AFTER_BLOCK},
        ${IMAGE_WITH_CALLOUTS_BLOCK},
        ${CATEGORIES_INTRO_BLOCK},
        ${CHART_BLOCK},
        ${MITOPURE_TRANSFORMATION_BLOCK},
        ${TESTIMONIAL_GROUP_BLOCK},
        ${PRESS_QUOTES_BLOCK},
        ${COMPARISON_TABLE_BLOCK},
        ${BANNER_BLOCK},
        ${REVIEWS_CAROUSEL_BLOCK},
        ${MEDIA_BLOCK},
        ${TEXT_BLOCK},
        ${KEY_INGREDIENTS_BLOCK},
        ${PRODUCT_COMPARISON_BLOCK},
        ${SOCIAL_MEDIA_CAROUSEL_BLOCK},
        ${FEATURE_LIST_BLOCK},
        ${VIBE_GALLERY_BLOCK},
        ${MITOCHONDRIAL_DECLINE_BLOCK},
        ${HALO_SHOWCASE_BLOCK}
      },
      bundleProducts[defined(product.product)] {
        _key,
        isFree,
        "product": product.product-> {
          ${PRODUCT_SLUG}
        },
        "variant": coalesce(
          product.variant-> { "gid": store.gid },
          product.product-> { "gid": store.variants[0]->store.gid }
        )
      },
      alternativeProduct {
        product {
          "title": ^.title,
          "subtitle": ^.subtitle,
          "label": ^.label,
          "slug": product->store.slug.current,
          ${productHref('product->')},
          "image": product->images[_type == "image"][0] {
            ${ASSET},
          },
          ${COALESCING_PRODUCT_VARIANT}
        }
      },
      complementaryProducts {
        title,
        products[] {
         "slug": product->store.slug.current,
          ${productHref('product->')},
         "image": product->images[_type == "image"][0] {
            ${ASSET},
          },
          ${COALESCING_PRODUCT_VARIANT}
        }
      },
      "bulletPoints": bulletPointsV2[] { ${RICH_TEXT} },
      disclaimer,
      ${PRODUCT_ACCORDION_FIELDS},
      ${SEE_IT_IN_ACTION},

      faq->{
        ${FAQ}
      },
      pressQuotes->{
        quotes[] {
          _key,
          logo {
            alt,
            "imageUrl": asset->url
          },
          quote,
          link
        }
      }
    }[0]
  `;

  const productPage: ProductPage = await client.fetch(query, {
    slug: queryKey[1],
  });

  return productPage;
};

export const useProductPage = (slug: string) =>
  useQuery({
    queryKey: [PRODUCT_PAGE_KEY, slug],
    queryFn: fetchProductPage,
  }) as ProductPageQuery;
